// Quick entity creation dialog for Needs and Benefits

import { useState } from 'react'
import { X } from 'lucide-react'
import type { SpaceNode } from '@ispot/shared'

interface AddEntityDialogProps {
  open: boolean
  entityType: 'need' | 'benefit'
  parentNode: SpaceNode | null
  onClose: () => void
  onSubmit: (data: { statement: string; parentId: string; entityType: 'need' | 'benefit' }) => void
}

export function AddEntityDialog({ open, entityType, parentNode, onClose, onSubmit }: AddEntityDialogProps) {
  const [statement, setStatement] = useState('')

  if (!open || !parentNode) return null

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (!statement.trim()) return
    onSubmit({ statement: statement.trim(), parentId: parentNode.id, entityType })
    setStatement('')
    onClose()
  }

  const label = entityType === 'need' ? 'Customer Need' : 'Benefit'
  const placeholder = entityType === 'need'
    ? 'e.g., Structured guidance through discovery process'
    : 'e.g., Know exactly what to do next'
  const parentLabel = entityType === 'need'
    ? `Under JTBD: ${parentNode.title}`
    : `Under Feature: ${parentNode.title}`

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-warm-900/20 backdrop-blur-sm" onClick={onClose}>
      <div className="bg-surface border border-border rounded-xl shadow-lg w-full max-w-md mx-4" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 pt-4 pb-2">
          <h3 className="font-display text-base font-semibold text-text">Add {label}</h3>
          <button onClick={onClose} className="text-text-muted hover:text-text p-1 rounded-md hover:bg-warm-100">
            <X size={16} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="px-5 pb-5">
          <p className="text-[12px] text-text-muted mb-3">{parentLabel}</p>

          <textarea
            autoFocus
            value={statement}
            onChange={e => setStatement(e.target.value)}
            placeholder={placeholder}
            rows={3}
            className="w-full px-3 py-2 text-[13px] border border-border rounded-lg bg-warm-50 text-text placeholder:text-text-muted/60 focus:outline-none focus:ring-2 focus:ring-core/20 focus:border-core resize-none"
          />

          <div className="flex justify-end gap-2 mt-3">
            <button type="button" onClick={onClose} className="text-[12px] text-text-muted px-3 py-1.5 rounded-md hover:bg-warm-100">
              Cancel
            </button>
            <button
              type="submit"
              disabled={!statement.trim()}
              className="text-[12px] text-warm-100 bg-warm-800 hover:bg-warm-900 disabled:opacity-40 disabled:cursor-not-allowed px-4 py-1.5 rounded-md transition-colors"
            >
              Create {label}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
